import { EllipsisVerticalIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useState } from 'react';
import AddUpdate from './AddUpdate';
import { getLeadsQuotes, getSingleLead } from '../../../api/requests';
import { Lead } from '../../../models/lead/Lead';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import Loader from '../../../components/Loader';
import {
  capitalizeSentence,
  getFormattedLeadSource,
  getPhoneNumberFromShortCodeFormat,
  getVehicleProtectionFromKey,
  showToast
} from '../../../utils/miscFunctions';
import useUserAccount from '../../../hooks/userAccountHook';
import { User } from '../../../models/User';
import useRoute from '../../../hooks/routesHook';
import EditLead from './EditLead';
import useModal from '../../../hooks/modalHook';
import BackButton from '../../../components/BackButton';
import AssignLead from './AssignLead';
import PopOnMe from '../../../components/elements/poponme';
import CreateTask from './CreateTask';
import PolicyStatus from '../../../components/elements/PolicyStatus';
import Stepper from '../../../components/ActivityStepper';
import GTable from '../../../components/elements/GTable';
import { SUCCESS_PAYMENT_STATUS_COLOR } from '../../../resources/constants';
import ChecklistStrength from '../../../components/CheckListStrength';
import { ILeadTask } from '../../../models/lead/ILead';
import TabNavItem from '../../../components/tabs/TabNavItem';
import TabContent from '../../../components/tabs/TabContent';
import { ITask } from '../../../resources/interfaces';
import LeadTagStatus from './LeadTagStatus';
import QuoteIframe from '../../../components/dashboard/quoteWidget/QuoteWidget';
import { Quote } from '../../../models/quote/Quote';
import { Policy } from '../../../models/policy/Policy';
import usePolicy from '../../../hooks/policyHook';
import { ITableRowAction } from '../../../components/elements/GTable/GTable';
import { ArrowLeft, Calendar, ClipboardPlus, Clock, Edit, ExternalLink, Mail, MessageSquarePlus, Phone, Receipt, Repeat, User as UserIcon } from 'lucide-react';
import clsx from 'clsx';

const LeadDetailsView = () => {
  const [lead, setLead] = useState<Lead>();
  const [quote, setQuote] = useState<[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('tasks');
  const [showUpdates, setShowUpdates] = useState<boolean>(false);
  const { getProfile } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = new User(getProfile());
  const { generatePolicyFromQuote } = usePolicy();

  const [completedChecklist, setCompletedChecklist] = useState<string[]>([]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchLead(id);
      fetchQuote(id)
    }
  }, [id]);

  useEffect(() => {
    id && fetchLead(id);
  }, [refresh]);

  useEffect(() => {
    if (lead) {
      setCompletedChecklist(
        lead.tasks
          ? lead?.tasks
            ?.filter(
              (task: ILeadTask) => task.completed && task.type && options?.includes(task?.type)
            )
            ?.map((tast: ILeadTask) => tast.type ?? '')
          : []
      );
    }
  }, [lead]);

  const fetchLead = (id: string) => {
    getSingleLead(id)
      .then((response) => {
        setLead(new Lead(response.data));
      })
      .catch(() => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchQuote = (id: string) => {
    getLeadsQuotes(id)
      .then((response) => {
        setQuote(response.data);
      })
      .catch(() => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const editLead = () => {
    lead &&
      updateModalStates({
        show: true,
        size: '2xl',
        children: (
          <EditLead
            lead={lead}
            onProceed={() => {
              setRefresh(true);
              closeModal();
              fetchLead(lead.id)
            }}
            onClose={closeModal}
            section={'ALL'}
          />
        ),
        showCloseButton: false,
        classes: '!p-0 r-[5px]'
      });
  };

  const assignLeadToAdmin = () => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignLead
          onProceed={() => {
            closeModal();
            if (id) fetchLead(id);
          }}
          onClose={closeModal}
          leadData={lead}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const showCreateTaskModal = (task?: ITask, selectedoption?: string) => {
    updateModalStates({
      show: true,
      size: 'xl',
      children: (
        <CreateTask
          onProceed={() => {
            if (id) fetchLead(id);
            closeModal();
          }}
          onClose={closeModal}
          id={id}
          isEdit={false}
          task={task}
          type={'LEAD'}
          selectedOption={selectedoption}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const updateTask = (task?: ITask, isEdit = true) => {
    updateModalStates({
      show: true,
      size: 'xl',
      children: (
        <CreateTask
          onProceed={() => {
            if (id) fetchLead(id);
            closeModal();
          }}
          onClose={closeModal}
          id={id}
          isEdit={isEdit}
          task={task}
          type={'LEAD'} />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const addQuote = () => {
    lead &&
      updateModalStates({
        show: true,
        size: 'lg',
        children: (
          <QuoteIframe
            onProceed={() => {
              if (id) fetchQuote(id);
              closeModal();
            }}
            onClose={closeModal}
            signup={lead}
          />
        ),
        static: true
      });
  };

  const getActions = () => {
    let actions = [
      {
        label: 'Edit',
        icon: <Edit className="h-4 w-4" />,
        callback: () => editLead()
      },
      {
        label: 'Re(assign) lead',
        icon: <Repeat className="h-4 w-4" />,
        callback: () => assignLeadToAdmin()
      },
      {
        label: 'Create task',
        icon: <ClipboardPlus className="h-4 w-4" />,
        callback: () => showCreateTaskModal()
      },

      {
        label: 'View / Add update',
        icon: <MessageSquarePlus className="h-4 w-4" />,
        callback: () => setShowUpdates(true)
      },
      {
        label: 'Get a quote',
        icon: <Receipt className="h-4 w-4" />,
        callback: () => addQuote()
      }
    ];

    return actions;
  };

  const getTableRowActions = (data: any) => {
    let actions = [
      {
        label: 'Update task',
        callback: () => updateTask(data, true)
      }
    ];

    return actions;
  };

  const handleCreatePolicy = (quote: Quote) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: <Loader message="Just a sec..." size="2xl" />
    });

    if (!quote.phone) {
      showToast.error('Phone number is required to create a policy from quote.');
      closeModal();
      return;
    }

    generatePolicyFromQuote(quote.id)
      .then((policy: Policy) => {
        if (policy) {
          showToast.success(
            'Policy created successfully from quote. Proceed to provide policy details.',
            
          );
          navigate(`${getDashboardBase()}/policies/${policy.ref}/edit`, { replace: true });
        }
      })
      .catch((error: any) => {
        closeModal();
        showToast.error(error.message ?? 'Failed to create policy from quote. Please try again later');
      });
  };

  const getQuoteTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [
      {
        label: 'View',
        callback: (quote: Quote) => {
          navigate(`${getDashboardBase()}/quotes/${quote.ref}`, { replace: true });
        },
        data: data
      }
    ];

    if (user.isAdministrator()) {
      actions.push({
        label: 'Create policy from quote',
        callback: (quote: Quote) => {
          handleCreatePolicy(quote);
        },
        data: data
      });
    }
    return actions;
  };

  const getStatus = (status: string) => {
    return status ? (
      <div
        className={`text-xs  w-fit px-5 flex items-center justify-center rounded ${SUCCESS_PAYMENT_STATUS_COLOR}`}>
        Done
      </div>
    ) : (
      <div className="text-xs  w-fit px-5 flex items-center justify-center rounded bg-gray-500 text-white">
        TO DO
      </div>
    );
  };

  const taskColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? capitalizeSentence(row.cell.value) : '-'}</span>;
            }
          },

          {
            Header: 'Description',
            accessor: 'description',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Type',
            accessor: 'type',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Assigned To',
            accessor: 'assignedTo.firstName',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value
                    ? capitalizeSentence(
                      row.row.original.assignedTo.firstName +
                      ' ' +
                      row.row.original.assignedTo.lastName
                    )
                    : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'completed',
            Cell: (row: any) => {
              return getStatus(row.cell.value);
            }
          },
          {
            Header: 'Due date',
            accessor: 'dueOn',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('MMM Do YY, h:mm a') : '-'}
                </span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  {getTableRowActions(row.row.original).length > 0 && (
                    <PopOnMe actions={getTableRowActions(row.row.original)}>
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </PopOnMe>
                  )}
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const quotesColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstName',
            Cell: (row: any) => {
              return !row.row.original.firstName && !row.row.original.lastName ? (
                <span>-</span>
              ) : (
                <span>
                  {row.row.original.firstName} {row.row.original.lastName}
                </span>
              );
            }
          },
          {
            Header: 'Source',
            accessor: 'source',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ? getFormattedLeadSource(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Protection Type',
            accessor: 'protectionType',
            Cell: (row: any) => {
              return (
                <span>
                  {' '}
                  {row.cell.value ? (
                    <>{getVehicleProtectionFromKey(row.cell.value)?.title}</>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },
          {
            Header: 'Date',
            accessor: 'paymentPlan',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value
                    ? moment(row.cell.value.createdOn).format('MMMM Do YYYY, h:mm:ss a')
                    : '-'}
                </span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getQuoteTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const options = [
    'Intro call',
    'Attempt to covert 1',
    'Attempt to convert 2',
    'Share First Digital Content ',
    'Share Second Digital Content',
    'Attempt to covert 3',
    'Attempt to convert 4',
    'Final Attempt to convert 5'
  ];

  return (
    <>
      {loading && (
        <div className="h-screen flex items-center justify-center">
          <Loader message="Fetching lead details. Please wait..." brollyLoaderFixed />
        </div>
      )}

      {!loading && lead && (
        <div className="p-6 pb-16 ">

          <div className=" w-full flex gap-3 justify-between">
            <div className=" w-fit flex gap-3 items-center">
              {/* Back button */}
              <div>
                <BackButton onClick={() => navigate(`${getDashboardBase()}/signups`)} />
              </div>

              {/* Lead header */}
              <div className="flex flex-col md:flex-row justify-between gap-3 items-start md:items-center ">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText ">
                  {lead.firstname} {lead.lastname}
                </h1>

                <div className="flex items-center justify-center gap-2">
                  <PolicyStatus
                    status={capitalizeSentence(lead?.leadStatus ?? '-')}
                    classes="rounded-full px-2  w-fit"
                  />

                  <div className="bg-primary-main min-w-fit py-1 px-2 rounded-full text-sm">
                    {lead?.leadSource
                      ? capitalizeSentence(getFormattedLeadSource(lead?.leadSource))
                      : '-'}
                  </div>

                  <LeadTagStatus status={lead?.tag ? capitalizeSentence(lead?.tag) : '-'} classes="rounded-full px-2 w-[120px] " />
                </div>
              </div>

            </div>
            {/* Actions */}
            <div>
              {getActions().length > 0 && (
                <PopOnMe actions={getActions()}>
                  <div className="cursor-pointer py-1 px-2 border border-primary-main rounded-full dark:text-darkBg-mainText">
                    Actions
                  </div>
                </PopOnMe>
              )}
            </div>
          </div>



          {/* Lead content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left column - Contact info */}
            <div className="col-span-1 space-y-6">
              <LeadDetailsItemCard title='Contact Information'>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-400 dark:text-darkBg-mainText mr-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">{lead.firstname} {lead.lastname}</div>
                      <div className="text-sm text-gray-500 dark:text-darkBg-mainText">ID: {lead.id}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 dark:text-darkBg-mainText mr-2" />
                    {lead.email ? (
                      <a href={`mailto:${lead.email}`} className="text-sm text-blue-600 hover:underline">{lead.email}</a>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-darkBg-mainText">-</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-400 dark:text-darkBg-mainText mr-2" />
                    {lead.phone ? (
                      <a href={`tel:${lead.phone}`} className="text-sm text-blue-600 dark:text-darkBg-textLink hover:underline">{getPhoneNumberFromShortCodeFormat(lead.phone)}</a>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-darkBg-mainText">-</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-darkBg-mainText mr-2" />
                    <div className="text-sm text-gray-500 dark:text-darkBg-mainText">Created on {moment(lead.createdOn).format('lll')}</div>
                  </div>

                  {/* {lead.lastContactedAt && (
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-500">Last contacted {moment(lead.lastContactedAt).format('lll')}</div>
                      </div>
                    )} */}
                </div>
              </LeadDetailsItemCard>
              <LeadDetailsItemCard title="Lead Details">
                <div className="space-y-3">
                  <LeadDetailsItem name="Source" value={lead.leadSource ? getFormattedLeadSource(lead.leadSource) : '-'} />
                  <LeadDetailsItem name="Insurance Type" value={lead?.insuranceType ? getVehicleProtectionFromKey(lead.insuranceType)?.title : '-'} />
                  <LeadDetailsItem name="Current Insurer" value={lead?.currentInsurer ? lead.currentInsurer : '-'} />
                  <LeadDetailsItem name="Insurance Status" value={lead?.leadStatus ? lead.leadStatus : '-'} />
                  <LeadDetailsItem name="Protection Type" value={lead?.insuranceType ? getVehicleProtectionFromKey(lead.insuranceType)?.title : '-'} />
                  <LeadDetailsItem name="Expiry Date" value={lead?.expiryDate ? moment(lead.expiryDate).format('lll') : '-'} />
                  <LeadDetailsItem name="Vehicle Number" value={lead?.registrationNumber ? lead.registrationNumber : '-'} />
                  <LeadDetailsItem name="Tag" value={lead?.tag ? capitalizeSentence(lead.tag) : '-'} />
                </div>
              </LeadDetailsItemCard>
            </div>

            {/* Right column - Notes, lead checklist, and activity */}
            <div className="col-span-1 md:col-span-2 space-y-6">
              <LeadDetailsItemCard title="Lead Checklist" classes='md-grid-cols-1'>
                <>
                  {/* lead Checklist */}
                  <div className=" bg-white dark:bg-darkBg dark:border dark:border-darkBg-border rounded-lg p-5">
                    <ChecklistStrength
                      options={options}
                      completedOptions={completedChecklist}
                      onOptionChange={(option: string, remove?: boolean) => {
                        if (remove) {
                          setCompletedChecklist(completedChecklist.filter((item) => item !== option));
                        } else {
                          setCompletedChecklist([...completedChecklist, option]);
                        }
                      }}
                      disableClick={true}
                      onClick={(option) => {
                        let task: any;
                        showCreateTaskModal(task, option);
                      }}
                    />
                  </div>

                </>

              </LeadDetailsItemCard>
              <LeadDetailsItemCard title="Activity feed">
                <div className=" md:col-span-2 bg-white dark:bg-darkBg dark:border dark:border-darkBg-border  p-5">
                  <div className=" py-5 overflow-y-auto h-full md:max-h-[20rem]">
                    {lead?.activityStreams && lead?.activityStreams?.length > 0 ? (
                      <div className="pl-4 ">
                        <Stepper
                          activityStreams={lead.activityStreams}
                          orientation="vertical"
                          currentStep={0}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-semibold">
                        No activity
                      </div>
                    )}
                  </div>
                </div>
              </LeadDetailsItemCard>


              {/* Notes */}
              <LeadDetailsItemCard title="Notes" classes='md:grid-cols-1'>
                <div className="bg-white dark:bg-darkBg dark:border dark:border-darkBg-border  p-6">
                  <div className="prose max-w-none">
                    <p className="text-sm text-gray-700 dark:text-darkBg-mainText w-full flex items-center justify-center font-semibold">No Note Available</p>
                  </div>
                </div>
              </LeadDetailsItemCard>


            </div>



          </div>

          <div className="bg-white dark:bg-darkBg dark:border dark:border-darkBg-border rounded-lg shadow overflow-hidden mt-6">
            <div className="flex items-center p-4">
              <TabNavItem
                id="tasks"
                title="Tasks"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
              <TabNavItem
                id="quotes"
                title="Quotes"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />

            </div>

            <div className="py-5">
              <TabContent id={'tasks'} activeTab={activeTab}>
                <h2 className="font-semibold text-2xl px-5 dark:text-darkBg-mainText">Tasks</h2>
                <div className="">
                  {lead.tasks && (
                    <GTable
                      columns={taskColumns}
                      data={lead.tasks}
                      classes="h-fit"
                      showSearch={false}
                      tableType={'task'}
                      stickyHeader
                    />
                  )}
                </div>
              </TabContent>
              <TabContent id={'quotes'} activeTab={activeTab}>
                <h2 className="font-semibold text-2xl px-5 dark:text-darkBg-mainText">Quotes</h2>
                <div className="">
                  {quote && (
                    <GTable
                      columns={quotesColumns}
                      data={quote}
                      classes="h-fit"
                      showSearch={false}
                      tableType={'quote'}
                      stickyHeader
                    />
                  )}
                </div>
              </TabContent>

              <TabContent id={'tab'} activeTab={activeTab}>
                <h2 className="font-semibold text-2xl px-5 dark:text-darkBg-mainText">Tasks</h2>
                <div className="">
                  {lead.tasks && (
                    <GTable
                      columns={taskColumns}
                      data={lead.tasks}
                      classes="h-fit"
                      showSearch={false}
                      tableType={'task'}
                      stickyHeader
                    />
                  )}
                </div>
              </TabContent>
            </div>
          </div>

          {showUpdates && (
            <AddUpdate
              show={showUpdates}
              onClose={() => {
                setShowUpdates(false);
              }}
              leadID={lead && lead.id}
              onProceed={() => { }}
              updates={lead?.leadUpdates ?? []}
              onLeadUpdated={(lead: Lead) => setLead(lead)}
            />
          )}
        </div >
      )}
      <ModalComponent />
    </>
  );
};



export default LeadDetailsView;


type LeadDetailsItemProps = {
  name?: string;
  value?: string | number | JSX.Element;
  children?: React.ReactNode;
  classes?: string;
};

interface IQuoteDetailsItemCard {
  title: string;
  classes?: string;
  children: JSX.Element;
  icon?: React.ReactElement;
}
const LeadDetailsItem = ({ name, value, children, classes }: LeadDetailsItemProps) => {
  return (
    <div className={clsx(`flex flex-col py-1 `, classes)}>
      <div className="text-gray-600 ">{name && `${name}:`}</div>
      <div className="flex items-center gap-1">
        <span className="truncate text-[14px] font-medium whitespace-nowrap">{value}</span>
      </div>
      {children}
    </div>
  );
};

const LeadDetailsItemCard = ({ title, classes, children, icon }: IQuoteDetailsItemCard): JSX.Element => {
  return (

    <div className="mt-5 bg-white dark:bg-darkBg dark:border-darkBg-border dark:border rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-darkBg-border bg-gray-50 dark:bg-darkBg dark:text-darkBg-mainText">
        <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
          {icon}
          {title}
        </h2>
      </div>
      <div
        className={clsx(
          'grid py-6  px-4 rounded-lg h-full grid-cols-1 gap-5 dark:text-darkBg-mainText ', classes ? classes : 'md:grid-cols-2 '
        )}>
        {children}
      </div>
    </div>
  );
};
